import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Avatar, Skeleton, TextLink, toast } from '@wit-support/ui';
import { Check, Copy, Crosshair, FileText, Image as ImageIcon, Layers, Megaphone, Newspaper, Users, } from 'lucide-react';
import { cn } from './lib/cn.js';
import { entityInitials } from './lib/initials.js';
/** Подпись вида, когда имени нет: «Страница», «Пиксель», … */
export const ENTITY_KIND_RU = {
    page: 'Страница',
    instagram: 'Instagram',
    pixel: 'Пиксель',
    audience: 'Аудитория',
    adaccount: 'Кабинет',
    post: 'Пост',
    campaign: 'Кампания',
    adset: 'Группа',
    ad: 'Объявление',
    creative: 'Креатив',
};
/** Для подсказки кнопки копирования: «ID страницы: …». */
const KIND_GENITIVE_RU = {
    page: 'страницы',
    instagram: 'Instagram',
    pixel: 'пикселя',
    audience: 'аудитории',
    adaccount: 'кабинета',
    post: 'поста',
    campaign: 'кампании',
    adset: 'группы',
    ad: 'объявления',
    creative: 'креатива',
};
/** Виды, у которых аватар (инициалы) рисуется по умолчанию. */
const AVATAR_KINDS = new Set(['page', 'instagram', 'adaccount']);
/** Значок вида без аватара (пиксель, аудитория, объекты запуска) — вместо кружка инициалов. */
const KIND_ICON = {
    pixel: Crosshair,
    audience: Users,
    post: FileText,
    campaign: Megaphone,
    adset: Layers,
    ad: Newspaper,
    creative: ImageIcon,
};
const kindRu = (kind) => Object.hasOwn(ENTITY_KIND_RU, kind) ? ENTITY_KIND_RU[kind] : kind;
const kindGenitiveRu = (kind) => Object.hasOwn(KIND_GENITIVE_RU, kind) ? KIND_GENITIVE_RU[kind] : kind;
/**
 * Название для показа: имя → `@ник` (Instagram без имени) → подпись вида.
 * `known` — пришло ли настоящее имя (иначе подпись вида рисуется
 * приглушённо).
 */
export function entityDisplayName(kind, name, username) {
    const n = name?.trim();
    if (n)
        return { text: n, known: true };
    const u = username?.trim();
    if (u)
        return { text: `@${u.replace(/^@/, '')}`, known: true };
    return { text: kindRu(kind), known: false };
}
/** Подсказка с id: «ID страницы: 1045…». */
export function entityIdTitle(kind, id) {
    return kind ? `ID ${kindGenitiveRu(kind)}: ${id}` : `ID: ${id}`;
}
/** Сколько держать галочку после удачного копирования. */
const COPIED_FEEDBACK_MS = 1500;
/**
 * Id сущности БЕЗ показа: маленькая кнопка-иконка «Скопировать ID», id — в
 * подсказке. Иконка проявляется, когда наведён или в фокусе ближайший
 * предок с классом `group/entity` (сам `EntityLabel` или строка списка), а
 * на устройствах без наведения видна всегда.
 */
export function EntityId({ id, kind, className }) {
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef(null);
    React.useEffect(() => () => {
        if (timer.current)
            clearTimeout(timer.current);
    }, []);
    async function copy() {
        try {
            await navigator.clipboard.writeText(id);
            toast.success('ID скопирован');
        }
        catch {
            toast.error('Не удалось скопировать');
            return;
        }
        setCopied(true);
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    }
    const Icon = copied ? Check : Copy;
    return (_jsx("button", { type: "button", "aria-label": "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C ID", title: entityIdTitle(kind, id), "data-testid": "entity-id", "data-id": id, "data-copied": copied ? 'true' : undefined, className: cn('inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-1)] align-middle', 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-text-primary)]', 'opacity-0 transition-opacity group-hover/entity:opacity-100 group-focus-within/entity:opacity-100 focus-visible:opacity-100 data-[copied=true]:opacity-100 [@media(hover:none)]:opacity-100', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]', className), onClick: (e) => {
            // строка может быть ссылкой или кликабельной карточкой
            e.preventDefault();
            e.stopPropagation();
            void copy();
        }, children: _jsx(Icon, { "aria-hidden": "true", className: cn('h-3 w-3', copied && 'text-[var(--color-state-success-fg)]') }) }));
}
export function EntityLabel({ kind, id, name, username, avatarSrc, preview, avatar, href, wrapName, variant = 'compact', avatarSize, loading = false, className, }) {
    const shown = entityDisplayName(kind, name, username);
    const nick = username?.trim().replace(/^@/, '') ?? '';
    const showUsername = kind === 'instagram' && nick !== '' && shown.text !== `@${nick}`;
    const withAvatar = preview != null || (avatar ?? AVATAR_KINDS.has(kind));
    const inline = variant === 'inline';
    const size = avatarSize ?? (inline ? 'xs' : 'sm');
    let nameNode = loading && !shown.known ? (_jsx(Skeleton, { className: cn('inline-block h-3 shrink-0 align-middle', inline ? 'w-16' : 'w-24') })) : (_jsx("span", { className: cn('min-w-0 truncate', !inline && 'font-medium', !shown.known && 'text-[var(--color-text-muted)]'), "data-testid": "entity-name", children: shown.text }));
    if (wrapName)
        nameNode = wrapName(nameNode);
    else if (href) {
        nameNode = (_jsx(TextLink, { href: href, external: true, tone: "inherit", className: "min-w-0 truncate", children: nameNode }));
    }
    const avatarNode = withAvatar
        ? (preview ??
            (AVATAR_KINDS.has(kind) ? (_jsx(EntityAvatar, { kind: kind, name: name ?? null, username: username ?? null, src: avatarSrc ?? null, size: size })) : (_jsx(EntityKindIcon, { kind: kind, size: size }))))
        : null;
    const title = [
        shown.known ? `${kindRu(kind)}: ${shown.text}` : shown.text,
        showUsername ? `@${nick}` : null,
        entityIdTitle(undefined, id),
    ]
        .filter(Boolean)
        .join('\n');
    return (_jsxs("span", { className: cn('group/entity inline-flex min-w-0 max-w-full items-center whitespace-nowrap align-middle leading-5', inline ? 'gap-1' : 'gap-1.5', className), title: title, "data-testid": "entity-label", "data-kind": kind, "data-id": id, children: [avatarNode, nameNode, showUsername ? (_jsxs("span", { className: "min-w-0 shrink-[2] truncate text-[var(--color-text-muted)]", "data-testid": "entity-username", children: ["@", nick] })) : null, _jsx(EntityId, { id: id, kind: kind })] }));
}
const AVATAR_SIZE = {
    xs: 'h-4 w-4 text-[8px]',
    sm: 'h-5 w-5 text-[9px]',
};
const ICON_SIZE = {
    xs: 'h-4 w-4 [&>svg]:h-3 [&>svg]:w-3',
    sm: 'h-5 w-5 [&>svg]:h-3.5 [&>svg]:w-3.5',
};
/** Значок вида в коробке размера аватара (пиксель, аудитория, объекты запуска). */
export function EntityKindIcon({ kind, size = 'sm', }) {
    const Icon = Object.hasOwn(KIND_ICON, kind) ? KIND_ICON[kind] : undefined;
    return (_jsx("span", { className: cn('inline-flex shrink-0 items-center justify-center text-[var(--color-text-muted)]', ICON_SIZE[size]), "data-testid": "entity-kind-icon", "aria-hidden": "true", children: Icon ? _jsx(Icon, {}) : null }));
}
/**
 * Аватар сущности: картинка по адресу, пока не пришла (или 404) — инициалы
 * имени (только буквы и цифры, без скобок и `|`), без имени — первая буква
 * ника, без ника — первая буква вида. Круглый — как у людей в Meta.
 */
export function EntityAvatar({ kind, name, username, src, size = 'sm', }) {
    const kindLetter = kindRu(kind).slice(0, 1).toUpperCase();
    const nickLetter = entityInitials(username, '').slice(0, 1);
    const initials = entityInitials(name, nickLetter || kindLetter);
    return (_jsxs(Avatar, { className: cn('shrink-0 rounded-full', AVATAR_SIZE[size]), "data-testid": "entity-avatar", "aria-hidden": "true", children: [src ? _jsx(Avatar.Image, { src: src, alt: "" }) : null, _jsx(Avatar.Fallback, { className: "font-medium leading-none", children: initials })] }));
}
//# sourceMappingURL=entity-label.js.map